import React, { useState, useEffect } from 'react';
import Navigation from '../Navigation/navigation';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';

const UserManagement = () => {
  const { user, isAuthenticated } = useAuth();
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    id: '',
    password: '',
    clinic_id: '',
    role: 'user',
    status: 'active'
  });

  // Load users and clinics
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      loadUsers();
      loadClinics();
    }
  }, [isAuthenticated, user]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/clinic-users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadClinics = async () => {
    try {
      const response = await api.get('/api/reference/clinics');
      setClinics(response.data);
    } catch (error) {
      console.error('Error loading clinics:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.id || !formData.clinic_id) {
      setError('Please fill in all required fields');
      return;
    }

    // Password is required for new users
    if (!editingUser && !formData.password) {
      setError('Password is required for new users');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if not provided
        }
        await api.put(`/api/clinic-users/${editingUser.user_id}`, updateData);
        setSuccessMessage('User updated successfully');
      } else {
        // Create new user
        await api.post('/api/clinic-users', formData);
        setSuccessMessage('User created successfully');
      }
      
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Error saving user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      id: userToEdit.user_id,
      password: '', // Don't show existing password
      clinic_id: userToEdit.clinic_id,
      role: userToEdit.role,
      status: userToEdit.status
    });
    setShowAddUser(true);
  };

  const handleDelete = async (userId) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete user "${userId}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setLoading(true);
    try {
      await api.delete(`/api/clinic-users/${userId}`);
      setSuccessMessage('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Error deleting user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      password: '',
      clinic_id: '',
      role: 'user',
      status: 'active'
    });
    setEditingUser(null);
    setShowAddUser(false);
  };

  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c.clinic_id === clinicId);
    return clinic ? clinic.clinic_name : 'Unknown Clinic';
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto p-5 pt-20">
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">User Management</h1>
              <p className="text-gray-600">
                Manage clinic user accounts.
              </p>
            </div>
            <button
              className="px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-lg flex items-center gap-2"
              onClick={() => setShowAddUser(true)}
            >
               Add New User
            </button>
          </div>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-50 border-l-4 bg-red-100 ring-2 ring-red-400 text-red-700 p-4 rounded-lg mb-6 font-medium">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-lg mb-6 font-medium">
            {successMessage}
          </div>
        )}

        {/* Add/Edit User Form Modal */}
        {showAddUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingUser ? 'Edit User' : 'Add New User'}
                </h3>
                <button 
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                  onClick={resetForm}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label htmlFor="user_id" className="block text-sm font-semibold text-gray-700 mb-2">
                    User ID *
                  </label>
                  <input
                    type="text"
                    id="user_id"
                    value={formData.id}
                    onChange={(e) => setFormData({...formData, id: e.target.value})}
                    placeholder="e.g., CLINIC001_USER1"
                    disabled={editingUser ? true : false}
                    required
                    className="w-full p-3 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {editingUser && (
                    <p className="text-sm text-gray-500 mt-1">
                      ℹ️ User ID cannot be changed for security and data integrity reasons.
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    Password {!editingUser ? '*' : ''}
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                    required={!editingUser}
                    className="w-full p-3 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div>
                  <label htmlFor="clinic_id" className="block text-sm font-semibold text-gray-700 mb-2">
                    Clinic *
                  </label>
                  <select
                    id="clinic_id"
                    value={formData.clinic_id}
                    onChange={(e) => setFormData({...formData, clinic_id: e.target.value})}
                    required
                    className="w-full p-3 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">Select Clinic</option>
                    {clinics.map((clinic) => (
                      <option key={clinic.clinic_id} value={clinic.clinic_id}>
                        {clinic.clinic_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    required
                    className="w-full p-3 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    required
                    className="w-full p-3 bg-gray-50 rounded-lg focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="button" 
                    className="flex-1 px-4 py-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="text-xl font-bold text-gray-800">Clinic Users</h3>
          </div>

          {loading && (
            <div className="p-8 text-center">
              <div className="text-gray-600">Loading users...</div>
            </div>
          )}
          
          {!loading && users.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-gray-600">No clinic users found. Click "Add New User" to create one.</p>
            </div>
          )}

          {!loading && users.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">User ID</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Clinic</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Role</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Created Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((userItem) => (
                    <tr key={userItem.user_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">{userItem.user_id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{getClinicName(userItem.clinic_id)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userItem.role === 'admin' 
                            ? 'px-3 py-2 bg-purple-100 text-purple-800' 
                            : 'px-3 py-2 bg-blue-100 text-blue-800'
                        }`}>
                          {userItem.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userItem.status === 'active' 
                            ? 'px-3 py-2 bg-green-100 text-green-800' 
                            : 'px-3 py-2 bg-red-100 text-red-800'
                        }`}>
                          {userItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(userItem.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            className="px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            onClick={() => handleEdit(userItem)}
                          >
                            Edit
                          </button>
                          <button
                            className="px-3 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            onClick={() => handleDelete(userItem.user_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
